import { LocalStorageKeys } from "@/constants/local-storage-keys";
import type { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import type { AgentFrameCommittedFoodDay } from "@/clients/streaming/agent-types";
import type { ChatImageData } from "@/features/ai-chat/utils/chat-image";
import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

export type ChatMessageRole = "user" | "agent" | "error";

export type ChatMessagePresentation = "background-note";

export type ChatMessageImage = ChatImageData;

export interface ChatMessageInterrupt {
  question: string;
  payload: Record<string, unknown>;
  /** True when this card shows figures the user may correct — a single meal
   *  add or swap. Always false for a multi-action pause, which is one
   *  all-or-nothing yes/no. Absent on messages persisted before this field
   *  existed, so treat undefined as not editable. */
  editable?: boolean;
}

export interface ChatMessagePlanFoodsCommitted {
  schemaVersion: 1;
  basketId: string;
  day: AgentFrameCommittedFoodDay;
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  text: string;
  images?: ChatMessageImage[];
  interrupt?: ChatMessageInterrupt;
  /** One date card from the authoritative plan_foods_committed SSE frame. */
  planFoodsCommitted?: ChatMessagePlanFoodsCommitted;
  /** Langfuse trace id for the agent turn that produced this message.
   *  Captured from the `done` SSE frame; required when posting feedback.
   *  null when Langfuse failed to mint a trace — feedback UI is disabled
   *  for the message in that case. Only present on `role: "agent"`. */
  traceId?: string | null;
  /** Stable server/client turn identity used to replace a partial streamed
   * response with the canonical result returned by turn reconciliation. */
  turnId?: string;
  clientTurnId?: string;
  segmentIndex?: number;
  /** Local display treatment selected by machine-readable stream metadata. */
  presentation?: ChatMessagePresentation;
}

export type PendingChatTurnStatus = "starting" | "running" | "reconciling" | "cancelling";

export interface PendingChatTurn {
  sessionId: string;
  clientTurnId: string;
  turnId?: string;
  userMessageId: string;
  status: PendingChatTurnStatus;
  startedAt: string;
  /** Exact prompt sent to AgentCore. This may contain contextual information
   * deliberately omitted from the visible user bubble and is required for an
   * idempotent retry if the initial POST acknowledgement is lost. */
  agentPrompt: string;
  imageCount: number;
}

const PERSISTED_MESSAGE_LIMIT = 50;

// Stale-after window for the cached conversation. If the user returns to
// /ai-chat more than this long after their last message, we discard both the
// local transcript and the server-side session id so the next visit starts
// fresh — otherwise the agent would keep replying as if continuing.
const CHAT_CACHE_TTL_MS = 5 * 60 * 1000;

export const isChatCacheExpired = (): boolean => {
  // An accepted or potentially-accepted server turn is authoritative. Never
  // discard the transcript/session needed to reconcile it, even if the user
  // returns after the normal conversation TTL.
  const pendingTurnRaw = localStorage.getItem(LocalStorageKeys.CHAT_PENDING_TURN_KEY);
  if (pendingTurnRaw) {
    try {
      if (JSON.parse(pendingTurnRaw) !== null) return false;
    } catch {
      // Preserve the conversation when a pending record cannot be decoded;
      // dropping it would make a potentially-running server turn unreachable.
      return false;
    }
  }
  const raw = localStorage.getItem(LocalStorageKeys.CHAT_LAST_ACTIVITY_KEY);
  if (!raw) return false;
  const lastActivity = Number(raw);
  if (!Number.isFinite(lastActivity)) return true;
  return Date.now() - lastActivity > CHAT_CACHE_TTL_MS;
};

const purgeExpiredCache = () => {
  localStorage.removeItem(LocalStorageKeys.CHAT_MESSAGES_KEY);
  localStorage.removeItem(LocalStorageKeys.CHAT_SESSION_ID_KEY);
  localStorage.removeItem(LocalStorageKeys.CHAT_LAST_ACTIVITY_KEY);
  localStorage.removeItem(LocalStorageKeys.CHAT_ORIGIN_KEY);
  localStorage.removeItem(LocalStorageKeys.CHAT_OPEN_KEY);
};

// Custom storage that strips `images` fields and caps at the last N messages
// before persisting. Bounds localStorage growth (full-fidelity base64 images
// would blow the ~5MB quota after a couple of photo-heavy sessions). On
// restore, sent photos are gone but text continuity is preserved; the
// just-taken photo is recovered separately via pendingChatImageAtom.
// `interrupt` is preserved so an unanswered Yes/No question survives reload.
const baseJSONStorage = createJSONStorage<ChatMessage[]>(() => localStorage);
const stripImage = (message: ChatMessage): ChatMessage => {
  const stripped: ChatMessage = {
    id: message.id,
    role: message.role,
    text: message.text,
  };
  if (message.interrupt) stripped.interrupt = message.interrupt;
  if (message.planFoodsCommitted) stripped.planFoodsCommitted = message.planFoodsCommitted;
  if (message.traceId !== undefined) stripped.traceId = message.traceId;
  if (message.turnId !== undefined) stripped.turnId = message.turnId;
  if (message.clientTurnId !== undefined) stripped.clientTurnId = message.clientTurnId;
  if (message.segmentIndex !== undefined) stripped.segmentIndex = message.segmentIndex;
  if (message.presentation !== undefined) stripped.presentation = message.presentation;
  return stripped;
};
const textOnlyMessageStorage = {
  ...baseJSONStorage,
  getItem: (key: string, initialValue: ChatMessage[]) => {
    if (isChatCacheExpired()) {
      purgeExpiredCache();
      return initialValue;
    }
    return baseJSONStorage.getItem(key, initialValue);
  },
  setItem: (key: string, newValue: ChatMessage[]) => {
    const sanitized = newValue.slice(-PERSISTED_MESSAGE_LIMIT).map(stripImage);
    baseJSONStorage.setItem(key, sanitized);
    if (sanitized.length > 0) {
      localStorage.setItem(LocalStorageKeys.CHAT_LAST_ACTIVITY_KEY, String(Date.now()));
    } else {
      localStorage.removeItem(LocalStorageKeys.CHAT_LAST_ACTIVITY_KEY);
    }
  },
};

// Session-id storage shares the same TTL: when the local transcript expires
// we also drop the session id so the next turn opens a fresh agent
// conversation instead of continuing the server-side memory of the stale one.
const baseSessionIdStorage = createJSONStorage<string>(() => localStorage);
const sessionIdStorage = {
  ...baseSessionIdStorage,
  getItem: (key: string, initialValue: string) => {
    if (isChatCacheExpired()) {
      purgeExpiredCache();
      return initialValue;
    }
    return baseSessionIdStorage.getItem(key, initialValue);
  },
};

/** UUID v4 session ID — persists across app boots and Android process kills
 *  so the agent's server-side conversation memory stays addressable, but
 *  expires alongside the transcript after CHAT_CACHE_TTL_MS of inactivity. */
export const chatSessionIdAtom = atomWithStorage<string>(
  LocalStorageKeys.CHAT_SESSION_ID_KEY,
  crypto.randomUUID(),
  sessionIdStorage,
);

/** Conversation transcript. Persisted via the text-only adapter above. */
export const chatMessagesAtom = atomWithStorage<ChatMessage[]>(
  LocalStorageKeys.CHAT_MESSAGES_KEY,
  [],
  textOnlyMessageStorage,
);

/** Which entry point started the cached conversation. Used by the bottom-bar
 *  Coach button to decide whether to resume the cached transcript or discard
 *  it and start fresh: it resumes only its own ("coach") conversations and
 *  resets anything seeded by a different entry point (e.g. the meal coach), so
 *  the central button always lands on its empty state with the suggestion
 *  pills. Shares the transcript's TTL — when the cache expires the origin is
 *  purged alongside it. */
export type ChatOrigin = "coach" | "meal" | "meal-picker" | "help";

const baseOriginStorage = createJSONStorage<ChatOrigin | null>(() => localStorage);
const originStorage = {
  ...baseOriginStorage,
  getItem: (key: string, initialValue: ChatOrigin | null) => {
    if (isChatCacheExpired()) {
      purgeExpiredCache();
      return initialValue;
    }
    return baseOriginStorage.getItem(key, initialValue);
  },
};

export const chatOriginAtom = atomWithStorage<ChatOrigin | null>(
  LocalStorageKeys.CHAT_ORIGIN_KEY,
  null,
  originStorage,
);

/** Photos currently sitting in the composer waiting to be sent. Persisted
 *  full-fidelity so the just-taken photo(s)
 *  survives an Android camera-launch process kill. */
export const pendingChatImageAtom = atomWithStorage<ChatMessageImage[]>(
  LocalStorageKeys.CHAT_PENDING_IMAGES_KEY,
  [],
);

/** Durable record of the one server turn currently starting/running. Kept
 * outside React component state so native suspension, WebView termination and
 * modal dismissal cannot make the turn unreachable. */
export const pendingChatTurnAtom = atomWithStorage<PendingChatTurn | null>(
  LocalStorageKeys.CHAT_PENDING_TURN_KEY,
  null,
);

/** Context prefix to silently prepend to the *next* user message streamed to
 *  the agent. Set just before navigating into /ai-chat from a contextual entry
 *  point (e.g. the My Plan meal coach button) so the agent learns which meal
 *  the user is asking about, without that text appearing in the visible
 *  transcript. In-memory only — it is consumed and cleared on the first send. */
export const pendingChatContextAtom = atom<string | null>(null);

/** A food selected outside the chat (for example, from Settings) that should
 * be submitted through the normal food-library chat flow when the chat opens.
 * In-memory only and cleared before it is consumed to prevent duplicate sends. */
export const pendingChatFoodAtom = atom<FoodLibraryMeal | null>(null);

/** Id of a locally-seeded agent message (e.g. the meal-coach greeting) that
 *  should fake-stream in — revealed with the typewriter cadence the next time
 *  the chat renders it, so it reads like a real streamed answer. In-memory
 *  only, like pendingChatContextAtom: it must survive the gap between a
 *  contextual entry point setting it (while the chat is unmounted) and the
 *  chat mounting. It deliberately does NOT live on the message itself — the
 *  transcript is persisted via atomWithStorage, which strips per-message flags
 *  and re-hydrates on mount, clobbering anything stored there. Cleared once the
 *  reveal finishes so the greeting streams exactly once. */
export const streamInMessageIdAtom = atom<string | null>(null);

/** Drives whether the AI-chat modal sheet is presented. The chat lives at the
 *  app root (one mount, mounted alongside `<BottomBar />`) and any entry point
 *  — bottom-bar Coach button, post-onboarding Meet NutriCoach finalize, meal-
 *  coach button, photo-restoration listener — flips this atom to true instead
 *  of pushing a route. The chevron-down in the header flips it back to false.
 *  Modeled after `ProfileSettingsModal` ("More") so iOS users get the familiar
 *  slide-up sheet UX. In-memory only. */
export const aiChatModalOpenAtom = atom<boolean>(false);

/** Message id the chat page should scroll to (centred) the next time it
 *  renders that message — set by the My Plan "just added" badge before it
 *  opens the sheet, consumed and cleared by the page. In-memory only. */
export const chatScrollToMessageIdAtom = atom<string | null>(null);

/** One-shot: armed by the post-onboarding "Meet NutriCoach" finalize to defer the
 *  welcome chat until the paywall decision is settled. `PaywallGuard` opens the chat
 *  and disarms this the moment it concludes no paywall is required — immediately when
 *  none is needed, or after the user subscribes / starts a trial. Gating the release
 *  on this flag keeps the guard from becoming a general chat opener: it is only ever
 *  true in the moments after onboarding finalize, so the guard's many other re-runs
 *  (login, app-foreground re-check, subscription tick-over) are no-ops. In-memory
 *  only — a reload clears it, matching today's "a closed welcome chat does not
 *  reopen". */
export const welcomeChatArmedAtom = atom<boolean>(false);

/** Pure decision for whether a boot-time auto-resume of the chat sheet should
 *  fire after an unexpected app restart. We resume only conversations that
 *  actually reached the server — i.e. that contain at least one user message,
 *  meaning a server-side AgentCore session exists to continue. Greeting-only
 *  opens (meal coach / post-onboarding welcome) are deliberately skipped: their
 *  in-memory meal context is gone after a restart and there is no server session
 *  to resume, so re-tapping the entry point (a fresh chat) is the right path.
 *  See `use-chat-resume-on-restart.ts`. */
/** Vestigial. The "what would you like to eat next?" meal-picker greeting
 *  (Figma node 273:1112) is now the default empty state (see
 *  `ai-chat/index.tsx`), so this flag no longer gates rendering. It is still
 *  written by the chat entry points (`useOpenCoachChat`,
 *  `useOpenMealPickerGreeting`, the New Chat header action) and cleared by
 *  Meet NutriCoach; those writes — and this atom — can be removed together as
 *  a follow-up. In-memory only. */
export const mealPickerGreetingPendingAtom = atom<boolean>(false);

export const shouldResumeChat = ({
  wasOpenOnBoot,
  isSignedIn,
  messages,
}: {
  wasOpenOnBoot: boolean;
  isSignedIn: boolean;
  messages: ChatMessage[];
}): boolean => wasOpenOnBoot && isSignedIn && messages.some((m) => m.role === "user");
